import { BaseSignatureInserter } from "./BaseSignatureInserter";
import { info, warn, error, debug } from "@/shared/services/logger";
import type { InsertResult } from "@/shared/types/inserter";

const SIGNATURE_START_MARKER = "<!-- HELPITECH_SIG_START -->";
const SIGNATURE_END_MARKER = "<!-- HELPITECH_SIG_END -->";

/**
 * Atrasos (em ms, relativos ao momento da inserção) em que o corpo é
 * reconferido em busca de assinaturas duplicadas.
 *
 * Uma única checagem fixa em 1.5s (tentativa anterior) se mostrou
 * insuficiente em uso real: o atraso do Outlook Clássico para terminar de
 * mesclar o corpo antigo — ao expandir um reply inline do reading pane ou
 * usar "Pop Out", ambos abrindo uma nova janela/runtime que reprocessa
 * `checkSignature` - não é fixo, varia com carga da máquina e sincronização
 * do rascunho. Por isso a reconferência roda várias vezes ao longo de uma
 * janela maior em vez de uma vez só; cada checagem só grava no corpo se
 * encontrar duplicata (senão é só uma leitura).
 */
const SELF_HEAL_CHECK_DELAYS_MS = [800, 1500, 3000, 5000, 8000, 12000, 18000];


/**
 * Implementa inserção de assinatura para o Outlook Clássico (Windows),
 * usando `prependAsync` como método principal, com conversão de
 * plain text → HTML quando necessário.
 *
 * A lógica compartilhada de orquestração fica em {@link BaseSignatureInserter}.
 *
 * @example
 * ```typescript
 * const inserter = new OutlookClassicSignatureInserter();
 * Office.actions.associate("checkSignature", (event) => inserter.insert(event));
 * ```
 */
export class OutlookClassicSignatureInserter extends BaseSignatureInserter {
  constructor() {
    super("OutlookClassicInserter");
  }

  /**
   * Insere HTML no corpo do item, removendo assinatura anterior marcada antes de aplicar.
   *
   * @param html - Conteúdo HTML da assinatura.
   * @param item - Item do Outlook.
   * @returns Promise com `InsertResult` detalhando sucesso/falha e método usado.
   *
   * @example
   * const result = await inserter.insertSignature(html, item);
   */
  insertSignature(html: string, item: Office.Item): Promise<InsertResult> {
    return new Promise((resolve) => {
      const body = (item as any).body;
      const isAppointment = (item as any).itemType === Office.MailboxEnums.ItemType.Appointment;

      if (isAppointment) {
        info(this.source, `[INSERT] Compromisso → setAsync (${html.length} chars).`);
        body.setAsync(html, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            info(this.source, "[INSERT] setAsync OK.");
            this.#scheduleDuplicateCheck(item);
            resolve({ success: true, method: "setAsync" });
          } else {
            error(this.source, `[INSERT] setAsync falhou — code=${r.error?.code}, msg=${r.error?.message}`);
            resolve({ success: false, method: "setAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
          }
        });
        return;
      }

      if (typeof body.getTypeAsync === "function") {
        body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) => {
          if (typeResult.status === Office.AsyncResultStatus.Succeeded && typeResult.value === Office.CoercionType.Text) {
            info(this.source, `[INSERT] Corpo em plain text → convertendo para HTML antes de inserir.`);
            body.getAsync(Office.CoercionType.Text, (getResult: Office.AsyncResult<string>) => {
              const existingText = getResult.status === Office.AsyncResultStatus.Succeeded
                ? (getResult.value || "")
                : "";
              const fullHtml = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER + "<br/>" + (existingText ? existingText.replace(/\n/g, "<br/>") : "");
              body.setAsync(fullHtml, { coercionType: Office.CoercionType.Html }, (setResult: Office.AsyncResult<void>) => {
                if (setResult.status === Office.AsyncResultStatus.Succeeded) {
                  info(this.source, "[INSERT] Conversão text→html OK.");
                  this.#scheduleDuplicateCheck(item);
                  resolve({ success: true, method: "setAsync" });
                } else {
                  error(this.source, `[INSERT] setAsync (convertido) falhou — code=${setResult.error?.code}`);
                  resolve({ success: false, method: "setAsync", errorCode: setResult.error?.code, errorMessage: setResult.error?.message });
                }
              });
            });
            return;
          }

          info(this.source, `[INSERT] Corpo HTML → limpando assinatura anterior e inserindo (${html.length} chars).`);
          body.getAsync(Office.CoercionType.Html, (getResult: Office.AsyncResult<string>) => {
            if (getResult.status !== Office.AsyncResultStatus.Succeeded) {
              error(this.source, `[INSERT] getAsync falhou — code=${getResult.error?.code}`);
              resolve({ success: false, method: "prependAsync", errorCode: getResult.error?.code, errorMessage: getResult.error?.message });
              return;
            }

            const existingHtml = getResult.value || "";
            const cleanedHtml = this.#removeExistingSignature(existingHtml);
            const markedSignature = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER;
            const fullHtml = markedSignature + cleanedHtml;

            debug(this.source, `[INSERT] Corpo original: ${existingHtml.length} chars, limpo: ${cleanedHtml.length} chars, final: ${fullHtml.length} chars.`);

            body.setAsync(fullHtml, { coercionType: Office.CoercionType.Html }, (setResult: Office.AsyncResult<void>) => {
              if (setResult.status === Office.AsyncResultStatus.Succeeded) {
                info(this.source, "[INSERT] setAsync (HTML limpo) OK.");
                this.#scheduleDuplicateCheck(item);
                resolve({ success: true, method: "setAsync" });
                return;
              }

              warn(this.source, `[INSERT] setAsync falhou — code=${setResult.error?.code}, msg=${setResult.error?.message}. Tentando limpar corpo antes do fallback.`);

              body.setAsync(cleanedHtml, { coercionType: Office.CoercionType.Html }, (cleanResult: Office.AsyncResult<void>) => {
                if (cleanResult.status === Office.AsyncResultStatus.Succeeded) {
                  info(this.source, "[INSERT] Corpo limpo com sucesso → prependAsync para nova assinatura.");
                  body.prependAsync(markedSignature + "<br/>", { coercionType: Office.CoercionType.Html }, (r2: Office.AsyncResult<void>) => {
                    if (r2.status === Office.AsyncResultStatus.Succeeded) {
                      info(this.source, "[INSERT] prependAsync OK (corpo limpo).");
                      this.#scheduleDuplicateCheck(item);
                      resolve({ success: true, method: "prependAsync" });
                    } else {
                      error(this.source, `[INSERT] prependAsync falhou após limpeza — code=${r2.error?.code}, msg=${r2.error?.message}`);
                      resolve({ success: false, method: "prependAsync", errorCode: r2.error?.code, errorMessage: r2.error?.message });
                    }
                  });
                } else {
                  error(this.source, `[INSERT] Limpeza do corpo falhou — code=${cleanResult.error?.code}. Fallback prependAsync sem limpeza.`);
                  body.prependAsync(markedSignature + "<br/>", { coercionType: Office.CoercionType.Html }, (r2: Office.AsyncResult<void>) => {
                    if (r2.status === Office.AsyncResultStatus.Succeeded) {
                      warn(this.source, "[INSERT] prependAsync OK (sem limpeza — assinaturas podem acumular).");
                      this.#scheduleDuplicateCheck(item);
                      resolve({ success: true, method: "prependAsync" });
                    } else {
                      error(this.source, `[INSERT] prependAsync falhou — code=${r2.error?.code}, msg=${r2.error?.message}`);
                      resolve({ success: false, method: "prependAsync", errorCode: r2.error?.code, errorMessage: r2.error?.message });
                    }
                  });
                }
              });
            });
          });
        });
        return;
      }

      info(this.source, `[INSERT] getTypeAsync indisponível → prependAsync (${html.length} chars).`);
      body.prependAsync("<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER + "<br/>", { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) {
          info(this.source, "[INSERT] prependAsync OK.");
          this.#scheduleDuplicateCheck(item);
          resolve({ success: true, method: "prependAsync" });
        } else {
          error(this.source, `[INSERT] prependAsync falhou — code=${r.error?.code}, msg=${r.error?.message}`);
          resolve({ success: false, method: "prependAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
        }
      });
    });
  }

  /**
   * Constrói a regex que localiza blocos de assinatura marcados
   * (`SIGNATURE_START_MARKER`...`SIGNATURE_END_MARKER`) no HTML do corpo.
   *
   * @returns Regex global/case-insensitive para os blocos marcados.
   */
  #signatureBlockRegex(): RegExp {
    return new RegExp(
      SIGNATURE_START_MARKER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
      "[\\s\\S]*?" +
      SIGNATURE_END_MARKER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "gi"
    );
  }

  /**
   * Remove uma assinatura anterior marcada do HTML do corpo.
   *
   * @param html - HTML do corpo do email.
   * @returns HTML sem a assinatura anterior marcada.
   */
  #removeExistingSignature(html: string): string {
    const cleaned = html.replace(this.#signatureBlockRegex(), "");
    debug(this.source, `[CLEAN] Assinatura anterior ${cleaned.length !== html.length ? "removida" : "não encontrada"}.`);
    return cleaned;
  }

  /**
   * Verifica se há mais de um bloco de assinatura marcado no HTML e,
   * se houver, mantém apenas o primeiro e remove os demais.
   *
   * Cobre o caso em que o Outlook Clássico mescla de forma assíncrona o
   * corpo antigo (já assinado) numa nova janela/runtime — depois que nossa
   * própria inserção já rodou — deixando dois blocos marcados no resultado
   * final.
   *
   * @param html - HTML do corpo do email a verificar.
   * @returns HTML corrigido e se uma duplicata foi encontrada.
   */
  #collapseDuplicateSignature(html: string): { cleaned: string; hadDuplicate: boolean } {
    const matches = html.match(this.#signatureBlockRegex());
    if (!matches || matches.length <= 1) {
      return { cleaned: html, hadDuplicate: false };
    }

    let kept = false;
    const cleaned = html.replace(this.#signatureBlockRegex(), (match) => {
      if (kept) return "";
      kept = true;
      return match;
    });
    return { cleaned, hadDuplicate: true };
  }

  /**
   * Agenda várias reconferências do corpo (ver {@link SELF_HEAL_CHECK_DELAYS_MS})
   * para detectar e corrigir assinaturas duplicadas inseridas pelo host de
   * forma assíncrona (expandir reply inline do reading pane, ou "Pop Out").
   *
   * Roda de forma independente do fluxo principal de inserção — não afeta
   * o resultado já resolvido para o chamador.
   *
   * @param item - Item do Outlook cujo corpo será reconferido.
   */
  #scheduleDuplicateCheck(item: Office.Item): void {
    for (const delay of SELF_HEAL_CHECK_DELAYS_MS) {
      setTimeout(() => this.#checkAndHealDuplicate(item), delay);
    }
  }

  /**
   * Lê o corpo atual do item e, se houver mais de um bloco de assinatura
   * marcado (ver {@link #collapseDuplicateSignature}), regrava o corpo
   * mantendo apenas o primeiro.
   *
   * @param item - Item do Outlook cujo corpo será verificado.
   */
  #checkAndHealDuplicate(item: Office.Item): void {
    const body = (item as any).body;
    if (!body?.getAsync) return;

    body.getAsync(Office.CoercionType.Html, (getResult: Office.AsyncResult<string>) => {
      if (getResult.status !== Office.AsyncResultStatus.Succeeded) return;

      const { cleaned, hadDuplicate } = this.#collapseDuplicateSignature(getResult.value || "");
      if (!hadDuplicate) return;

      warn(this.source, "[SELF-HEAL] Assinatura duplicada detectada (corpo mesclado pelo host) — removendo cópia extra.");
      body.setAsync(cleaned, { coercionType: Office.CoercionType.Html }, (setResult: Office.AsyncResult<void>) => {
        if (setResult.status === Office.AsyncResultStatus.Succeeded) {
          info(this.source, "[SELF-HEAL] Corpo corrigido com sucesso.");
        } else {
          error(this.source, `[SELF-HEAL] Falha ao corrigir corpo — code=${setResult.error?.code}, msg=${setResult.error?.message}`);
        }
      });
    });
  }
}
